import { useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import SelectField from "components/admin/SelectField";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import OnlineChart, { summarizeHistory } from "layouts/dashboard/OnlineChart";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, formatDuration, formatNumber } from "utils/format";

/*
 * Bố cục (người dùng: vận hành mở đầu ca để xem "game có ổn không, có đông không"):
 *   Cảnh báo (mất kết nối / đang bảo trì / tiến trình nền dừng) → 4 ô: Đang online · Cao nhất hôm nay · Máy chủ · Giờ vàng
 *   → [Sơ đồ online 24 giờ / 7 / 30 ngày] [Điều khiển: Bảo trì, Khởi động lại; Nâng cao gập sẵn]
 *   → [Người chơi theo map] [Thông tin kỹ thuật: RAM, luồng nền]
 */

const REFRESH_MS = 10000;
const HISTORY_REFRESH_MS = 60000;
const MAINTENANCE_PRESETS = [1, 3, 5, 10, 15, 30, 60];
const SHUTDOWN_PHRASE = "tắt máy chủ";

const normalize = (text) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/** Khung đang chạy, hoặc khung sắp tới; bảng golden_hour rỗng thì là ba trường cũ của server.json. */
function goldenHourCount(g) {
  if (!g) return "-";
  if (!g.usingTable) return `${g.start}h - ${g.end}h`;
  const slot = g.activeSlots?.[0] || g.next;
  return slot ? `${slot.startHour}h - ${slot.endHour}h` : "Không có";
}

function goldenHourAmount(g) {
  if (!g) return "";
  if (!g.usingTable) return `boss x${g.counter}`;
  if (g.active) return `exp x${g.expMul} · boss x${g.bossCounterMul}`;
  return g.next ? g.next.day : "";
}

function goldenHourLabel(g) {
  if (g?.active) return "đang diễn ra";
  if (g?.usingTable) return g.next ? "khung tiếp theo" : "chưa bật khung nào";
  return "chưa tới giờ";
}

/** "+12% so với giờ này hôm qua" */
function compareText(now, before) {
  if (now == null || before == null)
    return { color: "secondary", amount: "", label: "chưa có số liệu hôm qua để so" };
  if (before === 0) return { color: "secondary", amount: "", label: "giờ này hôm qua: 0 người" };
  const pct = Math.round(((now - before) / before) * 100);
  return {
    color: pct >= 0 ? "success" : "error",
    amount: `${pct >= 0 ? "+" : ""}${pct}%`,
    label: `so với giờ này hôm qua (${formatNumber(before)})`,
  };
}

function CollapsibleCard({ icon, iconColor, title, subtitle, open, onToggle, children }) {
  return (
    <Card sx={{ mt: 3 }}>
      <MDBox
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        px={2}
        py={1.5}
        display="flex"
        alignItems="center"
        gap={1}
        width="100%"
        sx={{
          cursor: "pointer",
          border: 0,
          background: "transparent",
          textAlign: "left",
          color: "inherit",
        }}
      >
        <Icon color={iconColor}>{icon}</Icon>
        <MDBox flex={1}>
          <MDTypography variant="button" fontWeight="bold" display="block">
            {title}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {subtitle}
          </MDTypography>
        </MDBox>
        <Icon>{open ? "expand_less" : "expand_more"}</Icon>
      </MDBox>
      <Collapse in={open}>
        <MDBox px={2} pb={2}>
          {children}
        </MDBox>
      </Collapse>
    </Card>
  );
}

CollapsibleCard.propTypes = {
  icon: PropTypes.string.isRequired,
  iconColor: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

function AdvancedRow({ title, description, children }) {
  return (
    <MDBox py={1.5}>
      <MDTypography variant="button" fontWeight="bold" display="block">
        {title}
      </MDTypography>
      <MDTypography variant="caption" color="text" display="block" mb={1}>
        {description}
      </MDTypography>
      {children}
    </MDBox>
  );
}

AdvancedRow.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function InfoLine({ label, value }) {
  return (
    <MDBox display="flex" justifyContent="space-between" gap={2} py={0.5}>
      <MDTypography variant="button" color="text">
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="medium" textAlign="right">
        {value}
      </MDTypography>
    </MDBox>
  );
}

InfoLine.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

function Dashboard() {
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const canControl = useCan(PERMISSIONS.SERVER_CONTROL);
  const status = useFetch(() => api.get("/api/admin/server/status"), [], REFRESH_MS);
  const maps = useFetch(() => api.get("/api/admin/server/maps"), [], REFRESH_MS);
  const historyFetch = useFetch(
    () => api.get("/api/admin/server/online-history"),
    [],
    HISTORY_REFRESH_MS
  );
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [maintenanceMinutes, setMaintenanceMinutes] = useState(5);
  const [maintenanceRestart, setMaintenanceRestart] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [shutdownOpen, setShutdownOpen] = useState(false);
  const [shutdownTyped, setShutdownTyped] = useState("");

  const s = status.data;
  let history;
  if (historyFetch.error) history = null;
  else if (historyFetch.data) history = historyFetch.data;
  const summary = summarizeHistory(historyFetch.data);
  const compare = compareText(s?.online, summary?.sameTimeYesterday);
  const deadThreads = (s?.threads || []).filter((t) => !t.isAlive);
  const onlineText = s ? `${formatNumber(s.online)} người đang chơi` : "người đang chơi";

  const serverAction = async (path, title, message, color = "error") => {
    if (!(await confirm({ title, message, color, confirmText: title }))) return;
    const { ok } = await run(
      () => api.post(path),
      (data) => (typeof data === "string" ? data : "Đã xong")
    );
    if (ok) status.reload(true);
  };

  const startMaintenance = async () => {
    const { ok } = await run(
      () =>
        api.post("/api/admin/server/maintenance", {
          minutes: Number(maintenanceMinutes),
          restart: maintenanceRestart,
        }),
      (data) => data
    );
    if (ok) {
      setMaintenanceOpen(false);
      status.reload(true);
    }
  };

  const shutdown = async () => {
    const { ok } = await run(
      () => api.post("/api/admin/server/shutdown"),
      (data) => (typeof data === "string" ? data : "Máy chủ đang tắt")
    );
    if (ok) {
      setShutdownOpen(false);
      setShutdownTyped("");
    }
  };

  const refreshField = () =>
    run(
      () => api.post("/api/admin/server/refresh-field"),
      (data) => `Đã đọc lại: exp ${data.expPercent}%, ngọc ${data.gemPercent}%`
    ).then(() => status.reload(true));

  const collectGarbage = () =>
    run(
      () => api.post("/api/admin/server/gc"),
      (data) => `Đã dọn bộ nhớ: ${data.beforeMb} MB → ${data.afterMb} MB`
    ).then(() => status.reload(true));

  const mapRows = (maps.data || []).filter((m) => m.players > 0);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="dashboard" />

        {/* ---- Cảnh báo cần chú ý ngay ---- */}
        <MDBox display="flex" flexDirection="column" gap={1} mb={2}>
          {status.error && (
            <Alert severity="error">
              Không lấy được tình trạng máy chủ game ({status.error}). Có thể máy chủ đang tắt hoặc
              đang khởi động lại — đợi 1–2 phút; vẫn lỗi thì báo kỹ thuật.
            </Alert>
          )}
          {s?.maintenance && (
            <Alert
              severity="warning"
              action={
                canControl && (
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={busy}
                    onClick={() =>
                      serverAction(
                        "/api/admin/server/maintenance/cancel",
                        "Huỷ bảo trì",
                        "Dừng đếm ngược bảo trì? Người chơi được báo và tiếp tục chơi bình thường.",
                        "warning"
                      )
                    }
                  >
                    Huỷ bảo trì
                  </MDButton>
                )
              }
            >
              Đang đếm ngược bảo trì — hết giờ máy chủ sẽ đá mọi người ra.
            </Alert>
          )}
          {deadThreads.length > 0 && (
            <Alert severity="error">
              Có {deadThreads.length} tiến trình nền đã dừng (
              {deadThreads.map((t) => t.name).join(", ")}). Một số tính năng có thể không chạy — báo
              kỹ thuật kiểm tra.
            </Alert>
          )}
          {s?.isOnlyAdminLogin && (
            <Alert severity="info">
              Máy chủ đang chỉ cho tài khoản admin đăng nhập, người chơi thường không vào được.
            </Alert>
          )}
        </MDBox>

        {/* ---- 4 ô số liệu ---- */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="people"
                title="Đang online"
                count={s ? formatNumber(s.online) : "-"}
                percentage={compare}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="trending_up"
                title="Cao nhất hôm nay"
                count={summary?.peak != null ? formatNumber(summary.peak) : "-"}
                percentage={{
                  color: "success",
                  amount: summary?.peakTime ? `lúc ${summary.peakTime}` : "",
                  label:
                    summary?.yesterdayPeak != null
                      ? `· hôm qua cao nhất ${formatNumber(summary.yesterdayPeak)}`
                      : "chưa có số liệu",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color={!s ? "dark" : s.maintenance ? "warning" : s.isRunning ? "success" : "error"}
                icon="dns"
                title="Máy chủ game"
                count={
                  !s ? "-" : s.maintenance ? "Sắp bảo trì" : s.isRunning ? "Đang chạy" : "Đã dừng"
                }
                percentage={{
                  color: "secondary",
                  amount: "",
                  label: s ? `chạy liên tục ${formatDuration(s.startTime)}` : "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color={s?.goldenHour?.active ? "warning" : "dark"}
                icon="whatshot"
                title="Giờ vàng"
                count={goldenHourCount(s?.goldenHour)}
                percentage={{
                  color: s?.goldenHour?.active ? "warning" : "secondary",
                  amount: goldenHourAmount(s?.goldenHour),
                  label: goldenHourLabel(s?.goldenHour),
                }}
              />
            </MDBox>
          </Grid>
        </Grid>

        {/* ---- Sơ đồ + điều khiển ---- */}
        <Grid container spacing={3} mt={0}>
          <Grid item xs={12} lg={8}>
            <OnlineChart history={history} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <Card sx={{ height: "100%", p: 2 }}>
              <MDTypography variant="h6">
                {canControl ? "Điều khiển máy chủ" : "Máy chủ"}
              </MDTypography>
              {s && (
                <MDBox mt={1}>
                  <InfoLine label="Giờ máy chủ" value={formatDate(s.serverTime)} />
                  <InfoLine label="Bật lên lúc" value={formatDate(s.startTime)} />
                  <InfoLine
                    label="Tỉ lệ exp / ngọc"
                    value={`${s.expPercent}% / ${s.gemPercent}%`}
                  />
                  {s.isServerTest && (
                    <Chip size="small" color="info" label="Đây là máy chủ test" sx={{ mt: 1 }} />
                  )}
                </MDBox>
              )}
              {canControl && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    {!s?.maintenance && (
                      <MDButton
                        fullWidth
                        variant="gradient"
                        color="warning"
                        disabled={busy || !s}
                        onClick={() => setMaintenanceOpen(true)}
                      >
                        <Icon>build</Icon>&nbsp;Bảo trì (báo trước cho người chơi)
                      </MDButton>
                    )}
                    <MDButton
                      fullWidth
                      variant="outlined"
                      color="error"
                      disabled={busy || !s}
                      onClick={() =>
                        serverAction(
                          "/api/admin/server/reboot",
                          "Khởi động lại ngay",
                          `Máy chủ sẽ lưu dữ liệu rồi khởi động lại NGAY, ${onlineText} bị ngắt kết nối không báo trước (mất khoảng 1–2 phút).\n\nMuốn báo trước cho người chơi thì dùng nút Bảo trì và bật "tự khởi động lại".`
                        )
                      }
                    >
                      <Icon>restart_alt</Icon>&nbsp;Khởi động lại ngay
                    </MDButton>
                    <MDTypography variant="caption" color="text">
                      Nên dùng Bảo trì: người chơi được báo đếm ngược trước khi bị đá ra.
                    </MDTypography>
                  </MDBox>
                </>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* ---- Map + kỹ thuật ---- */}
        <Grid container spacing={3} mt={0}>
          <Grid item xs={12} lg={8}>
            <PageCard title="Người chơi đang ở đâu" subtitle="Chỉ hiện map đang có người" noPadding>
              <AdminTable
                loading={maps.loading}
                rows={mapRows}
                rowKey="mapId"
                emptyText="Không có ai trong map nào"
                columns={[
                  { key: "name", label: "Map" },
                  {
                    key: "players",
                    label: "Số người",
                    align: "right",
                    render: (m) => formatNumber(m.players),
                  },
                  {
                    key: "zones",
                    label: "Các khu có người (đang có / tối đa)",
                    wrap: true,
                    render: (m) =>
                      m.zones
                        .filter((z) => z.players > 0)
                        .map((z) => `khu ${z.zoneId}: ${z.players}/${z.maxPlayer}`)
                        .join(" · "),
                  },
                ]}
              />
            </PageCard>
          </Grid>
          <Grid item xs={12} lg={4}>
            <PageCard
              title="Thông tin kỹ thuật"
              subtitle="Dành cho kỹ thuật, vận hành không cần theo dõi"
              color="secondary"
            >
              {s && (
                <>
                  <InfoLine
                    label="Bộ nhớ (RAM) đang dùng"
                    value={`${formatNumber(s.memoryMb)} MB`}
                  />
                  <InfoLine
                    label="Bộ nhớ quản lý (GC)"
                    value={`${formatNumber(s.gcMemoryMb)} MB`}
                  />
                  <InfoLine label="Kết nối mạng đang mở" value={formatNumber(s.sockets)} />
                  <InfoLine
                    label="Luồng xử lý"
                    value={`${s.processThreads} · ThreadPool ${s.threadPool.threadCount} (chờ ${s.threadPool.pendingWorkItemCount})`}
                  />
                  {s.threads.length > 0 && (
                    <>
                      <MDTypography
                        variant="button"
                        fontWeight="bold"
                        display="block"
                        mt={2}
                        mb={1}
                      >
                        Tiến trình nền
                      </MDTypography>
                      <MDBox display="flex" gap={0.5} flexWrap="wrap">
                        {s.threads.map((t) => (
                          <Chip
                            key={t.name}
                            size="small"
                            color={t.isAlive ? "success" : "error"}
                            label={`${t.name}: ${t.isAlive ? "chạy" : "DỪNG"}`}
                          />
                        ))}
                      </MDBox>
                    </>
                  )}
                </>
              )}
              {!s && (
                <MDTypography variant="button" color="text">
                  {status.error ? "Không lấy được thông tin." : "Đang tải…"}
                </MDTypography>
              )}
            </PageCard>
          </Grid>
        </Grid>

        {canControl && (
          <CollapsibleCard
            icon="warning_amber"
            iconColor="error"
            title="Nâng cao — chỉ dùng khi kỹ thuật yêu cầu"
            subtitle="Đọc lại tỉ lệ exp / ngọc, dọn bộ nhớ, tắt hẳn máy chủ"
            open={advancedOpen}
            onToggle={() => setAdvancedOpen((v) => !v)}
          >
            <AdvancedRow
              title="Đọc lại tỉ lệ exp / ngọc"
              description="Dùng khi kỹ thuật vừa sửa tỉ lệ exp / ngọc trong dữ liệu, áp dụng ngay không cần khởi động lại. Không ảnh hưởng người chơi."
            >
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                disabled={busy}
                onClick={refreshField}
              >
                <Icon>sync</Icon>&nbsp;Đọc lại tỉ lệ
              </MDButton>
            </AdvancedRow>
            <Divider sx={{ my: 0 }} />
            <AdvancedRow
              title="Dọn bộ nhớ"
              description="Giải phóng bộ nhớ máy chủ không còn dùng. Không ảnh hưởng người chơi."
            >
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                disabled={busy}
                onClick={collectGarbage}
              >
                <Icon>cleaning_services</Icon>&nbsp;Dọn bộ nhớ
              </MDButton>
            </AdvancedRow>
            <Divider sx={{ my: 0 }} />
            <AdvancedRow
              title="Tắt hẳn máy chủ"
              description="Máy chủ tắt và KHÔNG tự bật lại; phải có kỹ thuật bật lại trên máy. Web admin cũng mất kết nối."
            >
              <MDButton
                size="small"
                variant="gradient"
                color="error"
                disabled={busy}
                onClick={() => setShutdownOpen(true)}
              >
                <Icon>power_settings_new</Icon>&nbsp;Tắt máy chủ…
              </MDButton>
            </AdvancedRow>
          </CollapsibleCard>
        )}
      </MDBox>
      <Footer />

      {maintenanceOpen && (
        <ActionDialog
          open
          title="Bảo trì máy chủ"
          color="warning"
          busy={busy}
          submitText={`Bắt đầu đếm ngược ${maintenanceMinutes} phút`}
          onClose={() => setMaintenanceOpen(false)}
          onSubmit={startMaintenance}
        >
          <MDTypography variant="button" color="text">
            Người chơi thấy thông báo đếm ngược. Hết giờ, máy chủ lưu dữ liệu và đá mọi người ra.
          </MDTypography>
          <SelectField
            fullWidth
            label="Báo trước bao lâu"
            value={maintenanceMinutes}
            onChange={(e) => setMaintenanceMinutes(e.target.value)}
          >
            {MAINTENANCE_PRESETS.map((m) => (
              <MenuItem key={m} value={m}>
                {m} phút
              </MenuItem>
            ))}
          </SelectField>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Switch
                checked={maintenanceRestart}
                onChange={(e) => setMaintenanceRestart(e.target.checked)}
              />
            }
            label={
              maintenanceRestart
                ? "Hết giờ thì tự bật lại (bảo trì nhanh)"
                : "Hết giờ thì TẮT HẲN (cần kỹ thuật bật lại)"
            }
          />
          <Alert severity="warning">
            Sau {maintenanceMinutes} phút, {onlineText} sẽ bị đá ra.{" "}
            {maintenanceRestart
              ? "Máy chủ tự bật lại sau khoảng 1–2 phút."
              : "Máy chủ tắt hẳn cho tới khi kỹ thuật bật lại."}{" "}
            Trong lúc đếm ngược vẫn huỷ được.
          </Alert>
        </ActionDialog>
      )}

      {shutdownOpen && (
        <ActionDialog
          open
          title="Tắt hẳn máy chủ"
          color="error"
          busy={busy}
          disabled={normalize(shutdownTyped) !== normalize(SHUTDOWN_PHRASE)}
          submitText="Tắt máy chủ"
          onClose={() => {
            setShutdownOpen(false);
            setShutdownTyped("");
          }}
          onSubmit={shutdown}
        >
          <Alert severity="error">
            Máy chủ lưu dữ liệu rồi TẮT HẲN, {onlineText} bị ngắt ngay. Máy chủ không tự bật lại —
            phải có kỹ thuật bật trên máy. Web admin cũng mất kết nối.
          </Alert>
          <MDInput
            fullWidth
            autoFocus
            label={`Gõ "${SHUTDOWN_PHRASE}" để xác nhận`}
            value={shutdownTyped}
            onChange={(e) => setShutdownTyped(e.target.value)}
          />
        </ActionDialog>
      )}
    </DashboardLayout>
  );
}

export default Dashboard;
